import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, articles, getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = slug ? getArticlesByCategory(slug) : [];

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-semibold text-foreground-800">分类未找到</h1>
            <p className="mt-2 text-foreground-500">你访问的分类页面不存在</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const categoryJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://qinglanriyu.cn' },
          { '@type': 'ListItem', position: 2, name: category.name, item: `https://qinglanriyu.cn/category/${category.slug}` },
        ],
      },
      {
        '@type': 'CollectionPage',
        name: `${category.name} - 校园春色`,
        description: category.description,
        url: `https://qinglanriyu.cn/category/${category.slug}`,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50 font-body">
      <SEOHead
        title={`${category.name} - 校园春色`}
        description={category.description}
        keywords={`校园春色,${category.name},校园${category.name},青春校园生活,校园四季风光`}
        canonicalUrl={`/category/${category.slug}`}
        ogType="website"
        jsonLd={categoryJsonLd}
      />

      <Navbar />

      <main className="pt-20">
        <Breadcrumb items={[{ label: '首页', href: '/' }, { label: category.name }]} />

        <section className="w-full px-6 md:px-10 py-12">
          <div className="text-center mb-12">
            <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground-900">
              {category.name}
            </h1>
            <p className="mt-3 text-foreground-500 text-sm max-w-2xl mx-auto">
              {category.description}
            </p>
            <p className="mt-2 text-xs text-foreground-400">
              共 {category.articleCount} 篇文章
            </p>
          </div>

          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryArticles.map((article) => (
                <ArticleCard key={article.id} article={article} variant="default" />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-foreground-500">该分类下暂无文章</p>
            </div>
          )}
        </section>

        <section className="w-full px-6 md:px-10 py-12 bg-background-100">
          <div className="text-center mb-10">
            <h2 className="font-heading text-xl font-semibold text-foreground-800">探索更多分类</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {categories.filter(c => c.slug !== slug).map((cat) => (
              <a
                key={cat.slug}
                href={`/category/${cat.slug}`}
                className="block text-center p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 cursor-pointer transition-all duration-300"
              >
                <span className="text-sm font-medium text-foreground-700">{cat.name}</span>
              </a>
            ))}
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </div>
  );
}